import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Divider,
  Grid,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  useTheme as useMuiTheme,
} from '@mui/material';
import { useTheme } from '../contexts/ThemeContext';
import { settingsService } from '../services/settingsService';
import api from '../services/api';
import { alpha } from '@mui/material/styles';
import LoadingSpinner from '../components/LoadingSpinner';
import { ActivityTypes, logActivity } from '../utils/activityLogger';

const colorOptions = [
  { value: '#A80A0A' }, // Red
  { value: '#2563eb' }, // Blue
  { value: '#22c55e' }, // Green
  { value: '#a855f7' }, // Purple
  { value: '#f59e42' }, // Orange
  { value: '#f43f5e' }, // Pink
  { value: '#0f766e' }, // Teal
  { value: '#7c3aed' }, // Indigo
  { value: '#b91c1c' }, // Dark Red
  { value: '#15803d' }, // Dark Green
  { value: '#854d0e' }, // Amber
  { value: '#1e40af' }, // Dark Blue
];

function Settings() {
  const { primaryColor, fontSize, changePrimaryColor, changeFontSize } = useTheme();
  const muiTheme = useMuiTheme();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // State for settings
  const [charges, setCharges] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [transferMaintenanceMode, setTransferMaintenanceMode] = useState(false);
  const [payscribeThreshold, setPayscribeThreshold] = useState('');
  const [safeHavenThreshold, setSafeHavenThreshold] = useState('');
  const [transferFeeTier1, setTransferFeeTier1] = useState(''); // 100-4999: 15
  const [transferFeeTier2, setTransferFeeTier2] = useState(''); // 5000-49999: 50
  const [transferFeeTier3, setTransferFeeTier3] = useState(''); // 50000+: 100
  const [electronicLevy, setElectronicLevy] = useState('');
  const [freeTransferTimes, setFreeTransferTimes] = useState('');
  const [freeTransferTimesPerMonth, setFreeTransferTimesPerMonth] = useState('');
  const [kycDailyLimit, setKycDailyLimit] = useState('');
  const [kycMonthlyLimit, setKycMonthlyLimit] = useState('');
  const [kycAutoSuspend, setKycAutoSuspend] = useState(true);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [savingCharges, setSavingCharges] = useState(false);
  const [savingPayscribe, setSavingPayscribe] = useState(false);
  const [savingSafeHaven, setSavingSafeHaven] = useState(false);
  const [savingTransferFeeTier1, setSavingTransferFeeTier1] = useState(false);
  const [savingTransferFeeTier2, setSavingTransferFeeTier2] = useState(false);
  const [savingTransferFeeTier3, setSavingTransferFeeTier3] = useState(false);
  const [savingElectronicLevy, setSavingElectronicLevy] = useState(false);
  const [savingTransferMaintenance, setSavingTransferMaintenance] = useState(false);
  const [savingFreeTransferTimes, setSavingFreeTransferTimes] = useState(false);
  const [savingFreeTransferTimesPerMonth, setSavingFreeTransferTimesPerMonth] = useState(false);
  const [savingKycSettings, setSavingKycSettings] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [collections, setCollections] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);

  // Email tester state
  const [testEmail, setTestEmail] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);

  // Add after other useState declarations
  const [adminSettings, setAdminSettings] = useState(null);
  const [savingAdminSettings, setSavingAdminSettings] = useState(false);

  // Local state for push notifications
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushThreshold, setPushThreshold] = useState(1000);
  const [pushDiscount, setPushDiscount] = useState(2);
  const [pushMessage, setPushMessage] = useState('');
  // Local state for loyalty points
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(false);
  const [pointsPerNaira, setPointsPerNaira] = useState(0.001);
  const [redemptionRate, setRedemptionRate] = useState(0.01);

  // ATM Card settings
  const [atmCardEnabled, setAtmCardEnabled] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState('');
  const [cardCollectionFee, setCardCollectionFee] = useState('');
  const [cashbackEnabled, setCashbackEnabled] = useState(true);
  const [cashbackPercentage, setCashbackPercentage] = useState(100);
  const [savingAtmCardSettings, setSavingAtmCardSettings] = useState(false);

  // WhatsApp test states
  const [whatsappTestLoading, setWhatsappTestLoading] = useState(false);
  const [whatsappTestStatus, setWhatsappTestStatus] = useState(null);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');

  // Fetch initial settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await settingsService.getSettings();
        setCharges(settings.charges?.toString() || '');
        setMaintenanceMode(settings.maintenanceMode || false);
        setTransferMaintenanceMode(settings.transferMaintenanceMode || false);
        setPayscribeThreshold(settings.payscribeThreshold?.toString() || '');
        setSafeHavenThreshold(settings.safeHavenThreshold?.toString() || '');
        setTransferFeeTier1(settings.transferFeeTier1?.toString() || '');
        setTransferFeeTier2(settings.transferFeeTier2?.toString() || '');
        setTransferFeeTier3(settings.transferFeeTier3?.toString() || '');
        setElectronicLevy(settings.electronicLevy?.toString() || '');
        setFreeTransferTimes(settings.freeTransferTimes?.toString() || '');
        setFreeTransferTimesPerMonth(settings.freeTransferTimesPerMonth?.toString() || '');
        setKycDailyLimit(settings.kycDailyLimit?.toString() || '50000');
        setKycMonthlyLimit(settings.kycMonthlyLimit?.toString() || '200000');
        setKycAutoSuspend(settings.kycAutoSuspend ?? true);
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Fetch collections for backup
  useEffect(() => {
    // const fetchCollections = async () => {
    //   setCollectionsLoading(true);
    //   try {
    //     const res = await api.dashboard.getCollections();
    //     if (res.success && Array.isArray(res.collections)) {
    //       setCollections(res.collections);
    //     }
    //   } catch (error) {
    //     setSnackbar({ open: true, message: error.message || 'Failed to load collections', severity: 'error' });
    //   } finally {
    //     setCollectionsLoading(false);
    //   }
    // };
    // fetchCollections();
  }, []);

  // Fetch admin settings
  useEffect(() => {
    const fetchAdminSettings = async () => {
      try {
        const settings = await settingsService.getAdminSettings();
        setAdminSettings(settings);
        // Push notifications
        setPushEnabled(settings.pushNotifications?.enabled ?? false);
        setPushThreshold(settings.pushNotifications?.threshold ?? 1000);
        setPushDiscount(settings.pushNotifications?.discountPercent ?? 2);
        setPushMessage(settings.pushNotifications?.messageTemplate ?? '');
        // Loyalty points
        setLoyaltyEnabled(settings.loyaltyPoints?.enabled ?? false);
        setPointsPerNaira(settings.loyaltyPoints?.pointsPerNaira ?? 0.001);
        setRedemptionRate(settings.loyaltyPoints?.redemptionRate ?? 0.01);

        // Load ATM Card settings from the specific endpoint
        try {
          const atmCardSettings = await settingsService.getAtmCardSettings();
          if (atmCardSettings.status && atmCardSettings.data) {
            setAtmCardEnabled(atmCardSettings.data.system?.isCardRequestEnabled ?? false);
            setDeliveryFee(atmCardSettings.data.fees?.deliveryFee?.toString() || '5000');
            setCardCollectionFee(atmCardSettings.data.fees?.cardCollectionFee?.toString() || '15000');
            setCashbackEnabled(atmCardSettings.data.system?.enableDeliveryFeeCashback ?? true);
            setCashbackPercentage(atmCardSettings.data.system?.cashbackPercentage ?? 100);
          }
        } catch (error) {
          console.error('Error loading ATM card settings:', error);
          // Set defaults if loading fails
          setAtmCardEnabled(false);
          setDeliveryFee('5000');
          setCardCollectionFee('15000');
        }
      } catch (error) {
        setSnackbar({ open: true, message: error.message || 'Failed to load admin settings', severity: 'error' });
      }
    };
    fetchAdminSettings();
  }, []);

  // Handle charges update
  const handleChargesUpdate = async () => {
    try {
      setSavingCharges(true);
      const response = await settingsService.updateCharges(Number(charges));
      if (response.success) {
        // Log activity
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Updated system charges to ₦${charges}`,
          metadata: {
            setting: 'charges',
            oldValue: response.data.oldCharges,
            newValue: charges
          }
        });
        setSnackbar({
          open: true,
          message: response.message || 'Charges updated successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update charges',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update charges',
        severity: 'error'
      });
    } finally {
      setSavingCharges(false);
    }
  };

  // Handle maintenance mode update 
  const handleMaintenanceModeUpdate = async (checked) => {
    try {
      const response = await settingsService.updateMaintenanceMode(checked);
      if (response.success) {
        // Log activity
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Maintenance mode ${checked ? 'enabled' : 'disabled'}`,
          metadata: {
            setting: 'maintenanceMode',
            oldValue: !checked,
            newValue: checked
          }
        });
        setMaintenanceMode(checked);
        setSnackbar({
          open: true,
          message: response.message || `Maintenance mode ${checked ? 'enabled' : 'disabled'}`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update maintenance mode',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update maintenance mode',
        severity: 'error'
      });
    }
  };

  // Handle transfer maintenance mode update
  const handleTransferMaintenanceModeUpdate = async (checked) => {
    try {
      setSavingTransferMaintenance(true);
      const response = await settingsService.updateTransferMaintenanceMode(checked);
      if (response.success) {
        // Log activity
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Transfer maintenance mode ${checked ? 'enabled' : 'disabled'}`,
          metadata: {
            setting: 'transferMaintenanceMode',
            oldValue: !checked,
            newValue: checked
          }
        });
        setTransferMaintenanceMode(checked);
        setSnackbar({
          open: true,
          message: response.message || `Transfer maintenance mode ${checked ? 'enabled' : 'disabled'}`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update transfer maintenance mode',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update transfer maintenance mode',
        severity: 'error'
      });
    } finally {
      setSavingTransferMaintenance(false);
    }
  };

  // Handle Payscribe threshold update
  const handlePayscribeUpdate = async () => {
    try {
      setSavingPayscribe(true);
      const response = await settingsService.updatePayscribeThreshold(Number(payscribeThreshold));
      if (response.success) {
        // Log activity
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Updated Payscribe threshold to ₦${payscribeThreshold}`,
          metadata: {
            setting: 'payscribeThreshold',
            oldValue: response.data.oldThreshold,
            newValue: payscribeThreshold
          }
        });
        setSnackbar({
          open: true,
          message: response.message || 'Payscribe threshold updated successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update Payscribe threshold',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update Payscribe threshold',
        severity: 'error'
      });
    } finally {
      setSavingPayscribe(false);
    }
  };

  // Handle SafeHaven threshold update
  const handleSafeHavenUpdate = async () => {
    try {
      setSavingSafeHaven(true);
      const response = await settingsService.updateSafeHavenThreshold(Number(safeHavenThreshold));
      if (response.success) {
        // Log activity
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Updated SafeHaven threshold to ₦${safeHavenThreshold}`,
          metadata: {
            setting: 'safeHavenThreshold',
            oldValue: response.data.oldThreshold,
            newValue: safeHavenThreshold
          }
        });
        setSnackbar({
          open: true,
          message: response.message || 'SafeHaven threshold updated successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update SafeHaven threshold',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update SafeHaven threshold',
        severity: 'error'
      });
    } finally {
      setSavingSafeHaven(false);
    }
  };

  // Handle transfer fee tier updates
  const handleTransferFeeTier1Update = async () => {
    try {
      setSavingTransferFeeTier1(true);
      const response = await settingsService.updateTransferFeeTier1(Number(transferFeeTier1));
      if (response.success) {
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Updated transfer fee tier 1 (₦100-₦4,999) to ₦${transferFeeTier1}`,
          metadata: {
            setting: 'transferFeeTier1',
            oldValue: response.data.oldTransferFee,
            newValue: transferFeeTier1
          }
        });
        setSnackbar({
          open: true,
          message: response.message || 'Transfer fee tier 1 updated successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update transfer fee tier 1',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update transfer fee tier 1',
        severity: 'error'
      });
    } finally {
      setSavingTransferFeeTier1(false);
    }
  };

  const handleTransferFeeTier2Update = async () => {
    try {
      setSavingTransferFeeTier2(true);
      const response = await settingsService.updateTransferFeeTier2(Number(transferFeeTier2));
      if (response.success) {
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Updated transfer fee tier 2 (₦5,000-₦49,999) to ₦${transferFeeTier2}`,
          metadata: {
            setting: 'transferFeeTier2',
            oldValue: response.data.oldTransferFee,
            newValue: transferFeeTier2
          }
        });
        setSnackbar({
          open: true,
          message: response.message || 'Transfer fee tier 2 updated successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update transfer fee tier 2',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update transfer fee tier 2',
        severity: 'error'
      });
    } finally {
      setSavingTransferFeeTier2(false);
    }
  };

  const handleTransferFeeTier3Update = async () => {
    try {
      setSavingTransferFeeTier3(true);
      const response = await settingsService.updateTransferFeeTier3(Number(transferFeeTier3));
      if (response.success) {
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Updated transfer fee tier 3 (₦50,000+) to ₦${transferFeeTier3}`,
          metadata: {
            setting: 'transferFeeTier3',
            oldValue: response.data.oldTransferFee,
            newValue: transferFeeTier3
          }
        });
        setSnackbar({
          open: true,
          message: response.message || 'Transfer fee tier 3 updated successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update transfer fee tier 3',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update transfer fee tier 3',
        severity: 'error'
      });
    } finally {
      setSavingTransferFeeTier3(false);
    }
  };

  // Handle electronic levy update
  const handleElectronicLevyUpdate = async () => {
    try {
      setSavingElectronicLevy(true);
      const response = await settingsService.updateElectronicLevy(Number(electronicLevy));
      if (response.success) {
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Updated electronic transfer levy to ₦${electronicLevy}`,
          metadata: {
            setting: 'electronicLevy',
            oldValue: response.data.oldElectronicLevy,
            newValue: electronicLevy
          }
        });
        setSnackbar({
          open: true,
          message: response.message || 'Electronic transfer levy updated successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update electronic transfer levy',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update electronic transfer levy',
        severity: 'error'
      });
    } finally {
      setSavingElectronicLevy(false);
    }
  };

  // Handle free transfer times update (daily)
  const handleFreeTransferTimesUpdate = async () => {
    try {
      setSavingFreeTransferTimes(true);
      const response = await settingsService.updateFreeTransferTimes(Number(freeTransferTimes), undefined);
      if (response.success) {
        // Log activity
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Updated free transfer times to ${freeTransferTimes} per day`,
          metadata: {
            setting: 'freeTransferTimes',
            oldValue: response.data.oldFreeTransferTimes,
            newValue: freeTransferTimes
          }
        });
        setSnackbar({
          open: true,
          message: response.message || 'Free transfer times updated successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update free transfer times',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update free transfer times',
        severity: 'error'
      });
    } finally {
      setSavingFreeTransferTimes(false);
    }
  };

  // Handle free transfer times per month update
  const handleFreeTransferTimesPerMonthUpdate = async () => {
    try {
      setSavingFreeTransferTimesPerMonth(true);
      const response = await settingsService.updateFreeTransferTimes(undefined, Number(freeTransferTimesPerMonth));
      if (response.success) {
        // Log activity
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Updated free transfer times to ${freeTransferTimesPerMonth} per month`,
          metadata: {
            setting: 'freeTransferTimesPerMonth',
            newValue: freeTransferTimesPerMonth
          }
        });
        setSnackbar({
          open: true,
          message: response.message || 'Free transfer times per month updated successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update free transfer times per month',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update free transfer times per month',
        severity: 'error'
      });
    } finally {
      setSavingFreeTransferTimesPerMonth(false);
    }
  };

  const handleSaveAdminSettings = async () => {
    setSavingAdminSettings(true);
    try {
      await settingsService.updateAdminSettings({
        pushNotifications: {
          enabled: pushEnabled,
          threshold: pushThreshold,
          discountPercent: pushDiscount,
          messageTemplate: pushMessage
        },
        loyaltyPoints: {
          enabled: loyaltyEnabled,
          pointsPerNaira: pointsPerNaira,
          redemptionRate: redemptionRate
        },
        atmCard: {
          enabled: atmCardEnabled,
          deliveryFee: Number(deliveryFee) || 0,
          cardCollectionFee: Number(cardCollectionFee) || 0,
          cashbackEnabled: cashbackEnabled,
          cashbackPercentage: Number(cashbackPercentage) || 100
        }
      });
      setSnackbar({ open: true, message: 'Admin settings updated successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Failed to update admin settings', severity: 'error' });
    } finally {
      setSavingAdminSettings(false);
    }
  };

  // Handle KYC settings update
  const handleKycSettingsUpdate = async () => {
    try {
      setSavingKycSettings(true);
      const response = await settingsService.updateKycSettings(
        Number(kycDailyLimit),
        Number(kycMonthlyLimit),
        kycAutoSuspend
      );
      if (response) {
        // Log activity
        await logActivity({
          action: ActivityTypes.SETTINGS,
          details: `Updated KYC transfer limits: Daily ₦${kycDailyLimit}, Monthly ₦${kycMonthlyLimit}, Auto-suspend: ${kycAutoSuspend ? 'enabled' : 'disabled'}`,
          metadata: {
            setting: 'kycLimits',
            kycDailyLimit,
            kycMonthlyLimit,
            kycAutoSuspend
          }
        });
        setSnackbar({
          open: true,
          message: 'KYC transfer limits updated successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update KYC transfer limits',
        severity: 'error'
      });
    } finally {
      setSavingKycSettings(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle WhatsApp test
  const handleWhatsAppTest = async () => {
    if (!testPhoneNumber.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a phone number to test',
        severity: 'warning'
      });
      return;
    }

    try {
      setWhatsappTestLoading(true);
      setWhatsappTestStatus(null);

      const data = await api.testWhatsAppConnection(testPhoneNumber);

      if (data.success) {
        setWhatsappTestStatus({
          success: true,
          message: data.message || 'WhatsApp test message sent successfully!'
        });
        setSnackbar({
          open: true,
          message: 'WhatsApp test completed successfully',
          severity: 'success'
        });
      } else {
        setWhatsappTestStatus({
          success: false,
          message: data.message || 'WhatsApp test failed'
        });
        setSnackbar({
          open: true,
          message: data.message || 'WhatsApp test failed',
          severity: 'error'
        });
      }
    } catch (error) {
      const errorMessage = error.message || 'WhatsApp test failed';
      setWhatsappTestStatus({
        success: false,
        message: errorMessage
      });
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setWhatsappTestLoading(false);
    }
  };

  // Handle email test
  const handleTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }

    setTestingEmail(true);
    setEmailTestResult(null);

    try {
      const result = await settingsService.testEmailConnection(testEmail);

      setEmailTestResult({
        success: result.success,
        message: result.message,
        data: result.data,
        error: result.error,
        configStatus: result.configStatus
      });

      setSnackbar({
        open: true,
        message: result.message || (result.success ? 'Test email sent successfully!' : 'Failed to send test email'),
        severity: result.success ? 'success' : 'error'
      });
    } catch (error) {
      const errorMessage = error.message || error.response?.data?.message || 'Failed to test email connection';
      setEmailTestResult({
        success: false,
        message: errorMessage,
        error: error.response?.data?.error || errorMessage,
        configStatus: error.response?.data?.configStatus
      });
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingSpinner size="medium" />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          position: 'fixed',
          top: '80px !important',
          zIndex: 9999,
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            boxShadow: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Typography
        variant="h4"
        fontWeight={700}
        color="primary"
        gutterBottom
        sx={{
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          '&::after': {
            content: '""',
            flex: 1,
            height: '2px',
            background: theme => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            borderRadius: 1,
          }
        }}
      >
        Settings
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              boxShadow: theme => `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
              background: theme => `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mb: 2 }}>
              <FormControl sx={{ minWidth: 300, mb: 2 }} size="small" disabled={collectionsLoading}>
                <InputLabel id="select-collections-label">Select Collections</InputLabel>
                <Select
                  labelId="select-collections-label"
                  multiple
                  value={selectedCollections}
                  onChange={e => setSelectedCollections(e.target.value)}
                  input={<OutlinedInput label="Select Collections" />}
                  renderValue={selected => selected.join(', ')}
                >
                  {collections.map(col => (
                    <MenuItem key={col.name} value={col.name}>
                      <Checkbox checked={selectedCollections.indexOf(col.name) > -1} />
                      <ListItemText primary={col.name} secondary={col.size} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="secondary"
                onClick={async () => {
                  setDownloadingBackup(true);
                  try {
                    const result = await api.dashboard.backupDatabase(selectedCollections);
                    setSnackbar({
                      open: true,
                      message: result.message || 'Database backup downloaded successfully',
                      severity: 'success',
                    });
                  } catch (error) {
                    setSnackbar({
                      open: true,
                      message: error.message || 'Failed to download backup',
                      severity: 'error',
                    });
                  } finally {
                    setDownloadingBackup(false);
                  }
                }}
                disabled={downloadingBackup || collectionsLoading || selectedCollections.length === 0}
                startIcon={downloadingBackup ? <CircularProgress size={20} /> : null}
                sx={{ minWidth: 180 }}
              >
                {downloadingBackup ? 'Downloading...' : 'Download DB Backup'}
              </Button>
            </Box>
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{
                color: 'primary.main',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              Theme Settings
            </Typography>

            <Grid container spacing={4}>
              {/* Charges Field */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      fontWeight: 500,
                      color: 'text.primary',
                      mb: 2
                    }}
                  >
                    Charges (₦)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      value={charges}
                      onChange={e => setCharges(e.target.value)}
                      placeholder="Enter charges"
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={charges === '' || savingCharges}
                      onClick={handleChargesUpdate}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        textTransform: 'none',
                        boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: theme => `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {savingCharges ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Free Transfer Times Field (Daily) */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                    Free Transfer Times (per day)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      value={freeTransferTimes}
                      onChange={e => setFreeTransferTimes(e.target.value)}
                      placeholder="Enter number of free transfers per day"
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={freeTransferTimes === '' || savingFreeTransferTimes}
                      onClick={handleFreeTransferTimesUpdate}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        textTransform: 'none',
                        boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: theme => `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {savingFreeTransferTimes ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Free Transfer Times Field (Monthly) */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                    Free Transfer Times (per month)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      value={freeTransferTimesPerMonth}
                      onChange={e => setFreeTransferTimesPerMonth(e.target.value)}
                      placeholder="Enter number of free transfers per month"
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={freeTransferTimesPerMonth === '' || savingFreeTransferTimesPerMonth}
                      onClick={handleFreeTransferTimesPerMonthUpdate}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        textTransform: 'none',
                        boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: theme => `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {savingFreeTransferTimesPerMonth ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Transfer Fee Tiers */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                  Transfer Fee Tiers
                </Typography>
                <Grid container spacing={3}>
                  {/* Tier 1 */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                        Tier 1 (₦100 - ₦4,999)
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          type="number"
                          value={transferFeeTier1}
                          onChange={e => setTransferFeeTier1(e.target.value)}
                          placeholder="Enter fee for tier 1"
                          fullWidth
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={transferFeeTier1 === '' || savingTransferFeeTier1}
                          onClick={handleTransferFeeTier1Update}
                          sx={{ borderRadius: 2, px: 3, textTransform: 'none' }}
                        >
                          {savingTransferFeeTier1 ? <CircularProgress size={24} /> : 'Save'}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Tier 2 */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                        Tier 2 (₦5,000 - ₦49,999)
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          type="number"
                          value={transferFeeTier2}
                          onChange={e => setTransferFeeTier2(e.target.value)}
                          placeholder="Enter fee for tier 2"
                          fullWidth
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={transferFeeTier2 === '' || savingTransferFeeTier2}
                          onClick={handleTransferFeeTier2Update}
                          sx={{ borderRadius: 2, px: 3, textTransform: 'none' }}
                        >
                          {savingTransferFeeTier2 ? <CircularProgress size={24} /> : 'Save'}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Tier 3 */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                        Tier 3 (₦50,000+)
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          type="number"
                          value={transferFeeTier3}
                          onChange={e => setTransferFeeTier3(e.target.value)}
                          placeholder="Enter fee for tier 3"
                          fullWidth
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={transferFeeTier3 === '' || savingTransferFeeTier3}
                          onClick={handleTransferFeeTier3Update}
                          sx={{ borderRadius: 2, px: 3, textTransform: 'none' }}
                        >
                          {savingTransferFeeTier3 ? <CircularProgress size={24} /> : 'Save'}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Electronic Transfer Levy Field */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                    Electronic Transfer Levy (₦)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      value={electronicLevy}
                      onChange={e => setElectronicLevy(e.target.value)}
                      placeholder="Enter electronic transfer levy"
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={electronicLevy === '' || savingElectronicLevy}
                      onClick={handleElectronicLevyUpdate}
                      sx={{ borderRadius: 2, px: 3, textTransform: 'none' }}
                    >
                      {savingElectronicLevy ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                  </Box>
                </Box>
              </Grid>
              {/* KYC Transfer Limits Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                  KYC Transfer Limits (Non-Verified Users)
                </Typography>
              </Grid>

              {/* KYC Daily Limit */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                    Daily Transfer Limit (₦)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      value={kycDailyLimit}
                      onChange={e => setKycDailyLimit(e.target.value)}
                      placeholder="Enter daily limit"
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Maximum amount non-KYC users can transfer per day
                  </Typography>
                </Box>
              </Grid>

              {/* KYC Monthly Limit */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                    Monthly Transfer Limit (₦)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      value={kycMonthlyLimit}
                      onChange={e => setKycMonthlyLimit(e.target.value)}
                      placeholder="Enter monthly limit"
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Maximum amount non-KYC users can transfer per month
                  </Typography>
                </Box>
              </Grid>

              {/* KYC Auto-Suspend Toggle */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={kycAutoSuspend}
                        onChange={(e) => setKycAutoSuspend(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Auto-suspend users who exceed daily limit"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 7 }}>
                    Automatically suspend non-KYC users who attempt to exceed the daily limit
                  </Typography>
                </Box>
              </Grid>

              {/* Save KYC Settings Button */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={kycDailyLimit === '' || kycMonthlyLimit === '' || savingKycSettings}
                    onClick={handleKycSettingsUpdate}
                    sx={{ borderRadius: 2, px: 4, textTransform: 'none' }}
                  >
                    {savingKycSettings ? <CircularProgress size={24} /> : 'Save KYC Settings'}
                  </Button>
                </Box>
              </Grid>


              {/* Maintenance Mode Switch */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={maintenanceMode}
                        onChange={(e) => handleMaintenanceModeUpdate(e.target.checked)}
                        color="primary"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            '& + .MuiSwitch-track': {
                              backgroundColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 500, color: 'text.primary' }}>
                        Maintenance Mode
                      </Typography>
                    }
                  />
                </Box>
              </Grid>

              {/* Transfer Maintenance Mode Switch */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={transferMaintenanceMode}
                        onChange={(e) => handleTransferMaintenanceModeUpdate(e.target.checked)}
                        color="primary"
                        disabled={savingTransferMaintenance}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            '& + .MuiSwitch-track': {
                              backgroundColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 500, color: 'text.primary' }}>
                        Transfer Maintenance Mode
                      </Typography>
                    }
                  />
                </Box>
              </Grid>

              {/* Payscribe Threshold */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      fontWeight: 500,
                      color: 'text.primary',
                      mb: 2
                    }}
                  >
                    Payscribe Threshold (₦)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      value={payscribeThreshold}
                      onChange={e => setPayscribeThreshold(e.target.value)}
                      placeholder="Enter Payscribe threshold"
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={payscribeThreshold === '' || savingPayscribe}
                      onClick={handlePayscribeUpdate}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        textTransform: 'none',
                        boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: theme => `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {savingPayscribe ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* SafeHaven Threshold */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                    SafeHaven Threshold (₦)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      value={safeHavenThreshold}
                      onChange={e => setSafeHavenThreshold(e.target.value)}
                      placeholder="Enter SafeHaven threshold"
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={safeHavenThreshold === '' || savingSafeHaven}
                      onClick={handleSafeHavenUpdate}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        textTransform: 'none',
                        boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: theme => `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {savingSafeHaven ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Primary Color */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                    Primary Color
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {colorOptions.map((color) => (
                      <Box
                        key={color.value}
                        onClick={() => changePrimaryColor(color.value)}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: color.value,
                          cursor: 'pointer',
                          border: primaryColor === color.value ? '3px solid' : 'none',
                          borderColor: 'primary.main',
                          boxShadow: primaryColor === color.value ? 3 : 1,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: 4,
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>

              {/* Font Size */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.primary', mb: 2 }}>
                    Font Size
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={fontSize}
                      onChange={(e) => changeFontSize(e.target.value)}
                      sx={{
                        '& .MuiFormControlLabel-root': {
                          marginLeft: 0,
                          marginRight: 2,
                        },
                        '& .MuiRadio-root': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      <FormControlLabel value="small" control={<Radio />} label="Small" />
                      <FormControlLabel value="medium" control={<Radio />} label="Medium" />
                      <FormControlLabel value="large" control={<Radio />} label="Large" />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* WhatsApp Test Section */}
      <Divider sx={{ my: 4 }} />
      <Typography variant="h6" fontWeight={600} color="primary" gutterBottom>
        WhatsApp Integration Test
      </Typography>
      <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Test WhatsApp Connection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Test the WhatsApp integration by sending a test message to verify the eBulkSMS connection is working properly.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <TextField
                label="Test Phone Number"
                value={testPhoneNumber}
                onChange={(e) => setTestPhoneNumber(e.target.value)}
                placeholder="e.g., 2348012345678"
                fullWidth
                size="small"
                helperText="Enter phone number in international format (2348012345678)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleWhatsAppTest}
              disabled={whatsappTestLoading || !testPhoneNumber.trim()}
              startIcon={whatsappTestLoading ? <CircularProgress size={20} /> : null}
              sx={{
                borderRadius: 2,
                px: 3,
                textTransform: 'none',
                boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: theme => `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {whatsappTestLoading ? 'Testing...' : 'Test WhatsApp Connection'}
            </Button>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Test Status
            </Typography>

            {whatsappTestStatus && (
              <Box sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: whatsappTestStatus.success ? 'success.light' : 'error.light',
                border: 1,
                borderColor: whatsappTestStatus.success ? 'success.main' : 'error.main',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip
                    label={whatsappTestStatus.success ? 'Success' : 'Failed'}
                    color={whatsappTestStatus.success ? 'success' : 'error'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {new Date().toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.primary">
                  {whatsappTestStatus.message}
                </Typography>
              </Box>
            )}

            {!whatsappTestStatus && (
              <Box sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: 'grey.50',
                border: 1,
                borderColor: 'grey.300',
                textAlign: 'center'
              }}>
                <Typography variant="body2" color="text.secondary">
                  No test results yet. Click "Test WhatsApp Connection" to verify the integration.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" fontWeight={600} color="primary" gutterBottom>
        Push Notification & Loyalty Points Settings
      </Typography>
      <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        <Grid container spacing={4}>
          {/* Push Notifications */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>Push Notifications</Typography>
            <FormControlLabel
              control={<Switch checked={pushEnabled} onChange={e => setPushEnabled(e.target.checked)} color="primary" />}
              label="Enable Push Notifications"
            />
            <TextField
              label="Low Meter Threshold (₦)"
              type="number"
              value={pushThreshold}
              onChange={e => setPushThreshold(Number(e.target.value))}
              fullWidth
              sx={{ my: 2 }}
            />
            <TextField
              label="Discount (%)"
              type="number"
              value={pushDiscount}
              onChange={e => setPushDiscount(Number(e.target.value))}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Message Template"
              value={pushMessage}
              onChange={e => setPushMessage(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ mb: 2 }}
              helperText="Use {discount} and {balance} as placeholders."
            />
          </Grid>
          {/* Loyalty Points */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>Loyalty Points</Typography>
            <FormControlLabel
              control={<Switch checked={loyaltyEnabled} onChange={e => setLoyaltyEnabled(e.target.checked)} color="primary" />}
              label="Enable Loyalty Points"
            />
            <TextField
              label="Points per Naira"
              type="number"
              value={pointsPerNaira}
              onChange={e => setPointsPerNaira(Number(e.target.value))}
              fullWidth
              sx={{ my: 2 }}
              inputProps={{ step: 0.001 }}
            />
            <TextField
              label="Redemption Rate (₦ per point)"
              type="number"
              value={redemptionRate}
              onChange={e => setRedemptionRate(Number(e.target.value))}
              fullWidth
              sx={{ mb: 2 }}
              inputProps={{ step: 0.01 }}
            />
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAdminSettings}
            disabled={savingAdminSettings}
          >
            {savingAdminSettings ? <CircularProgress size={20} /> : 'Save Settings'}
          </Button>
        </Box>
      </Paper>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" fontWeight={600} color="primary" gutterBottom>
        ATM Card Settings
      </Typography>
      <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        <Grid container spacing={4}>
          {/* ATM Card Enable/Disable */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              ATM Card Feature
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={atmCardEnabled}
                  onChange={(e) => setAtmCardEnabled(e.target.checked)}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      '& + .MuiSwitch-track': {
                        backgroundColor: 'primary.main',
                      },
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Enable ATM Card Requests
                </Typography>
              }
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              When enabled, users can request physical ATM cards through the mobile app.
            </Typography>
          </Grid>

          {/* Delivery Fee */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Delivery Fee (₦)
            </Typography>
            <TextField
              label="Delivery Fee"
              type="number"
              value={deliveryFee}
              onChange={(e) => setDeliveryFee(e.target.value)}
              placeholder="e.g., 5000"
              fullWidth
              size="small"
              helperText="Cost for delivering the physical card to user's address"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          {/* Card Collection Fee */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Card Collection Fee (₦)
            </Typography>
            <TextField
              label="Card Collection Fee"
              type="number"
              value={cardCollectionFee}
              onChange={(e) => setCardCollectionFee(e.target.value)}
              placeholder="e.g., 15000"
              fullWidth
              size="small"
              helperText="Cost for the physical ATM card itself"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          {/* Cashback Feature */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Cashback Feature
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={cashbackEnabled}
                  onChange={(e) => setCashbackEnabled(e.target.checked)}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      '& + .MuiSwitch-track': {
                        backgroundColor: 'primary.main',
                      },
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Enable Delivery Fee Cashback
                </Typography>
              }
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Users will receive cashback when they activate their ATM card.
            </Typography>
          </Grid>

          {/* Cashback Percentage */}
          {cashbackEnabled && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Cashback Percentage (%)
              </Typography>
              <TextField
                label="Cashback Percentage"
                type="number"
                value={cashbackPercentage}
                onChange={(e) => setCashbackPercentage(e.target.value)}
                placeholder="e.g., 100"
                fullWidth
                size="small"
                inputProps={{ min: 0, max: 100 }}
                helperText="Percentage of delivery fee to give as cashback (0-100%)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
          )}

          {/* Fee Summary */}
          <Grid item xs={12}>
            <Box sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: 'primary.light',
              border: 1,
              borderColor: 'primary.main',
            }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'primary.main' }}>
                Fee Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Delivery Fee:
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="primary.main">
                    ₦{deliveryFee || '0'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Card Collection Fee:
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="primary.main">
                    ₦{cardCollectionFee || '0'}
                  </Typography>
                </Grid>
                {cashbackEnabled && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Cashback on Activation:
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="success.main">
                      ₦{Math.round(((Number(deliveryFee) || 0) * (Number(cashbackPercentage) || 100)) / 100)} ({cashbackPercentage}%)
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Total Amount:
                    </Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      ₦{(Number(deliveryFee) || 0) + (Number(cardCollectionFee) || 0)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAdminSettings}
            disabled={savingAdminSettings}
            sx={{
              borderRadius: 2,
              px: 4,
              textTransform: 'none',
              boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: theme => `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {savingAdminSettings ? <CircularProgress size={20} /> : 'Save ATM Card Settings'}
          </Button>
        </Box>
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* Email Server Tester */}
      <Typography variant="h6" fontWeight={600} color="primary" gutterBottom>
        📧 Email Server Tester
      </Typography>
      <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Test your email server configuration by sending a test email to verify that emails are working correctly.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Test Email Address"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter email address to send test email"
              fullWidth
              size="medium"
              helperText="Enter a valid email address where you want to receive the test email"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTestEmail}
              disabled={testingEmail || !testEmail || !testEmail.includes('@')}
              fullWidth
              sx={{
                height: '56px',
                borderRadius: 2,
                px: 4,
                textTransform: 'none',
                boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: theme => `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {testingEmail ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Testing...
                </>
              ) : (
                'Send Test Email'
              )}
            </Button>
          </Grid>

          {/* Email Test Result */}
          {emailTestResult && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: emailTestResult.success
                    ? alpha(muiTheme.palette.success.main, 0.1)
                    : alpha(muiTheme.palette.error.main, 0.1),
                  border: 1,
                  borderColor: emailTestResult.success
                    ? muiTheme.palette.success.main
                    : muiTheme.palette.error.main,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {emailTestResult.success ? (
                    <>
                      <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                        ✅ Email Test Successful
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="h6" color="error.main" sx={{ fontWeight: 600 }}>
                        ❌ Email Test Failed
                      </Typography>
                    </>
                  )}
                </Box>

                <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
                  {emailTestResult.message}
                </Typography>

                {emailTestResult.data && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Test Details:
                    </Typography>
                    <Box sx={{
                      bgcolor: 'background.paper',
                      p: 2,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem'
                    }}>
                      {emailTestResult.data.server && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Server:</strong> {emailTestResult.data.server}
                        </Typography>
                      )}
                      {emailTestResult.data.port && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Port:</strong> {emailTestResult.data.port}
                        </Typography>
                      )}
                      {emailTestResult.data.recipient && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Recipient:</strong> {emailTestResult.data.recipient}
                        </Typography>
                      )}
                      {emailTestResult.data.messageId && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Message ID:</strong> {emailTestResult.data.messageId}
                        </Typography>
                      )}
                      {emailTestResult.data.timestamp && (
                        <Typography variant="body2">
                          <strong>Timestamp:</strong> {new Date(emailTestResult.data.timestamp).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {emailTestResult.error && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 600, mb: 1 }}>
                      Error Details:
                    </Typography>
                    <Typography variant="body2" sx={{
                      bgcolor: 'background.paper',
                      p: 2,
                      borderRadius: 1,
                      color: 'error.main',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem'
                    }}>
                      {emailTestResult.error}
                    </Typography>
                  </Box>
                )}

                {emailTestResult.configStatus && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Configuration Status:
                    </Typography>
                    <Box sx={{
                      bgcolor: 'background.paper',
                      p: 2,
                      borderRadius: 1,
                      fontSize: '0.875rem'
                    }}>
                      {Object.entries(emailTestResult.configStatus).map(([key, value]) => (
                        <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                          <strong>{key}:</strong> {value ? '✅ Configured' : '❌ Missing'}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
}

export default Settings;